import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, RotateCcw, Save, Search, FileDown } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import SidePanel from "@/components/calendar/SidePanel";

// =====================================================================
// Settings → Email Templates (V2 Round 4)
//
// Lists clinic-scoped email templates and lets clinic admins edit the
// subject + body. Variable chips ({{patient_name}}, {{clinic_name}}, …)
// are clickable — they insert at the current cursor position in the
// focused field.
//
// Calls:
//   list_email_templates()
//   update_email_template(template_key, subject, body)
//   reset_email_template_to_default(template_key)
//   import_missing_email_templates()
//
// All gated on clinic.settings.manage.
// =====================================================================

// Friendly labels + descriptions per template_key. Anything not in this
// catalog falls back to the raw key.
const KEY_LABELS = {
  "appointment.reminder_24hr":         { label: "24-hour appointment reminder", desc: "Sent the day before a Confirmed appointment." },
  "appointment.confirmation_request":  { label: "Please-confirm reminder",      desc: "Sent shortly after a booking when confirmation is still pending." },
  "appointment.cancellation_notice":   { label: "Cancellation notice",          desc: "Sent when an appointment is cancelled." },
  "payment.link_initial":              { label: "First payment link",           desc: "Sent right after checkout when there's a remaining balance." },
  "payment.link_reminder":             { label: "Payment reminder",             desc: "Follow-up nudge when a payment link is still unpaid." },
  "payment.receipt":                   { label: "Receipt",                      desc: "Sent after a successful payment." },
  "recall.priority_outreach":          { label: "Recall outreach",              desc: "Sent when a patient is due for a recall visit." },
  "patient.welcome":                   { label: "Welcome",                      desc: "Sent to new patients after their first visit." },
  "billing.invoice":                   { label: "Invoice",                      desc: "Sent for clinic-side billing (B2B / corporate)." },
};

export default function SettingsEmailTemplates() {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [rows, setRows]       = useState([]);
  const [search, setSearch]   = useState("");
  const [panel, setPanel]     = useState(null); // null | { row }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_email_templates");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load email templates."); return; }
    setRows(data ?? []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const friendly = KEY_LABELS[r.template_key]?.label ?? r.template_key;
      return friendly.toLowerCase().includes(q)
          || r.template_key.toLowerCase().includes(q)
          || (r.subject || "").toLowerCase().includes(q);
    });
  }, [rows, search]);

  async function handleImportMissing() {
    const { data, error: e } = await supabase.rpc("import_missing_email_templates");
    if (e) { toast.error(e.message ?? "Could not import."); return; }
    if (data?.imported > 0) toast.success(`Imported ${data.imported} new template${data.imported === 1 ? "" : "s"}.`);
    else                    toast.info("Already up to date.");
    load();
  }

  // Access denied (RPC raises 42501 if caller lacks clinic.settings.manage)
  if (error && /permission denied/i.test(error)) {
    return (
      <div data-testid="settings-email-templates-denied" className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <Mail className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">You need <code className="text-xs bg-slate-100 px-1 rounded">clinic.settings.manage</code> to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-email-templates">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Email templates</h1>
            <p className="text-xs text-slate-500">Customize subject + body for the emails Dental OS sends to patients on your behalf.</p>
          </div>
          <button type="button" onClick={handleImportMissing}
            data-testid="email-tpl-import"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded">
            <FileDown className="w-3.5 h-3.5" />Import new defaults
          </button>
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input type="search" placeholder="Search templates…" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="email-tpl-search"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        </div>
      </div>

      {/* Error (non-permission) */}
      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <Mail className="w-8 h-8 mx-auto text-slate-300" />
          <p className="text-sm text-slate-600 mt-3">No email templates yet. Click <span className="font-semibold">Import new defaults</span> to seed them from the platform.</p>
        </div>
      ) : (
        <ul className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="email-tpl-list">
          {filtered.map((row) => {
            const friendly = KEY_LABELS[row.template_key] ?? { label: row.template_key, desc: null };
            return (
              <li key={row.id}>
                <button type="button" onClick={() => setPanel({ row })}
                  data-testid={`email-tpl-row-${row.template_key}`}
                  className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate">{friendly.label}</p>
                      {row.is_overridden ? (
                        <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">Customized</span>
                      ) : null}
                    </div>
                    {friendly.desc ? <p className="text-xs text-slate-500 mt-0.5">{friendly.desc}</p> : null}
                    <p className="text-xs text-slate-400 mt-1 truncate">Subject: {row.subject || "(none)"}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Edit panel */}
      <SidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel ? (KEY_LABELS[panel.row.template_key]?.label ?? panel.row.template_key) : ""}
      >
        {panel ? (
          <EditTemplate
            row={panel.row}
            onSaved={() => { setPanel(null); load(); }}
            onCancel={() => setPanel(null)}
          />
        ) : null}
      </SidePanel>
    </div>
  );
}

function EditTemplate({ row, onSaved, onCancel }) {
  const [subject, setSubject] = useState(row.subject ?? "");
  const [body, setBody]       = useState(row.body ?? "");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const subjectRef = useRef(null);
  const bodyRef    = useRef(null);
  const lastFocusedRef = useRef("body"); // "subject" | "body"

  const variables = Array.isArray(row.variables) ? row.variables : [];

  function insertVariable(varName) {
    const placeholder = `{{${varName}}}`;
    const target = lastFocusedRef.current === "subject" ? subjectRef.current : bodyRef.current;
    if (!target) return;
    const start = target.selectionStart ?? target.value.length;
    const end   = target.selectionEnd   ?? target.value.length;
    const before = target.value.slice(0, start);
    const after  = target.value.slice(end);
    const next   = before + placeholder + after;
    if (lastFocusedRef.current === "subject") setSubject(next);
    else                                       setBody(next);
    // Restore caret + focus after React re-renders.
    requestAnimationFrame(() => {
      target.focus();
      const pos = start + placeholder.length;
      target.setSelectionRange(pos, pos);
    });
  }

  async function handleSave() {
    if (!body.trim()) { setError("Body is required."); return; }
    setError(null); setBusy(true);
    const { error: e } = await supabase.rpc("update_email_template", {
      p_template_key: row.template_key,
      p_subject: subject || null,
      p_body: body,
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Save failed."); return; }
    toast.success("Template saved.");
    onSaved?.();
  }

  async function handleReset() {
    if (!window.confirm("Reset this template back to the platform default? Your edits will be replaced.")) return;
    setError(null); setBusy(true);
    const { error: e } = await supabase.rpc("reset_email_template_to_default", {
      p_template_key: row.template_key,
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Reset failed."); return; }
    toast.success("Reset to platform default.");
    onSaved?.();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="tpl-subject">Subject</label>
          <input
            id="tpl-subject"
            ref={subjectRef}
            data-testid="email-tpl-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => { lastFocusedRef.current = "subject"; }}
            placeholder="(no subject)"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="tpl-body">Body</label>
          <textarea
            id="tpl-body"
            ref={bodyRef}
            data-testid="email-tpl-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => { lastFocusedRef.current = "body"; }}
            rows={14}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
          />
        </div>

        {variables.length > 0 ? (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Variables (click to insert)</p>
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  data-testid={`email-tpl-var-${v}`}
                  className="text-xs font-mono bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-100 px-2 py-1 rounded"
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Variables are filled in automatically when an email is sent.
              Click a chip to drop the placeholder where your cursor is.
            </p>
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
        ) : null}
      </div>

      {/* Sticky footer */}
      <div className="border-t border-slate-200 px-5 py-3 flex items-center gap-2 bg-white">
        <button type="button" onClick={handleReset} disabled={busy}
          data-testid="email-tpl-reset"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50">
          <RotateCcw className="w-3.5 h-3.5" />Reset to default
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="email-tpl-cancel"
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={busy}
            data-testid="email-tpl-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
